import React, { useEffect } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    FormControl,
    FormControlLabel,
    Checkbox,
    withTheme,
    TextField
} from '@material-ui/core';
import { useFormik } from 'formik';
import config from '../../config';
import { doSwaggerCall } from '../../hooks/useSwaggerClient';
import { ErrorIcon } from '../../icons';
import { useGuestFields } from '../../hooks/useGuestFields';
import GuestFieldInput, { validateGuestFields } from '../GuestFieldInput';
import { validatePhone, formatPhone } from '../../helpers/phoneUtils';

export const validateSignupForm = (values, guest_fields) => {
    const errors = {};

    if (!values.firstname) {
        errors.firstname = 'First name is required.';
    }

    if (!values.lastname) {
        errors.lastname = 'Last name is required.';
    }

    const phoneError = validatePhone(values.phone);
    if (phoneError) {
        errors.phone = phoneError;
    }

    if (!values.email) {
        errors.email = 'Email is required.';
    }

    if (values.password !== undefined && values.password === '') {
        errors.password = 'Password is required.';
    }

    if (values.termsAndConditions !== undefined && values.termsAndConditions === false) {
        errors.terms = 'Terms and conditions needs to be accepted.';
    }

    // validate guest fields (required + phone/email format)
    const guestFieldErrors = validateGuestFields(values.guest_field_info, guest_fields);
    Object.assign(errors, guestFieldErrors);

    return errors;
};

export const handleGuestFieldEventValue = (values, setFieldValue, field, value) => {
    const guestInfo = values.guest_field_info || [];
    const index = guestInfo.findIndex(e => e.id === field.id);
    if (index === -1) {
        guestInfo.push({ id: field.id, name: field.name, value });
    } else {
        guestInfo[index] = { ...guestInfo[index], value };
    }
    setFieldValue('guest_field_info', guestInfo);
};

function SignUpModal({ theme, setLoggedInUser, showModal, MODAL_TYPE }) {
    const [guest_fields] = useGuestFields();

    const termsAndConditionsLabel = (
        <span>
            By checking this box I agree to the{' '}
            <a
                href={`${config.assetsUrl}/termsandconditions.pdf`}
                target="_blank"
                rel="noopener noreferrer"
            >
                Terms & Conditions
            </a>{' '}
            set forth by Activity Provider which include but are not limited to the sudden change or
            cancellation of my activity.
        </span>
    );
    const [apiError, setApiError] = React.useState('');

    const onSubmit = values => {
        const req = doSwaggerCall('auth', 'signup', {
            userData: {
                firstname: values.firstname,
                lastname: values.lastname,
                phone: values.phone,
                email: values.email,
                password: values.password,
                termsAndConditions: values.termsAndConditions,
                guest_field_info: values.guest_field_info
            }
        });
        req.then(data => {
            if (data.user) {
                setLoggedInUser(data.user);
                showModal(MODAL_TYPE.NONE);
            } else {
                setApiError(data.message || 'An error occurred during sign-up.');
            }
        })
            .catch(error => {
                setApiError(error.response.body.message || 'An error occurred during sign-up.');
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const requiredGuestFields = guest_fields.filter(
        field =>
            field?.agentOnly !== true &&
            (field?.useForRegistration === true || field?.required === true)
    );

    const {
        values,
        isSubmitting,
        errors,
        isValid,
        dirty,
        handleSubmit,
        setSubmitting,
        setFieldValue
    } = useFormik({
        initialValues: {
            firstname: '',
            lastname: '',
            phone: '',
            email: '',
            password: '',
            termsAndConditions: false,
            guest_field_info: requiredGuestFields.map(field => ({
                id: field.id,
                name: field.name,
                value: ''
            }))
        },
        validate: values => validateSignupForm(values, requiredGuestFields),
        onSubmit: values => onSubmit(values)
    });

    useEffect(() => {
        setApiError('');
    }, [values]);

    const handleInputChange = (event, field) => {
        const { value } = event.target;
        setFieldValue(field, value);
    };

    return (
        <Dialog
            aria-labelledby="sign-in-modal"
            maxWidth="xs"
            open
            onClose={() => showModal(MODAL_TYPE.NONE)}
        >
            <DialogTitle>Sign-up</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Grid container spacing={2}>
                        {apiError && (
                            <Grid
                                item
                                container
                                spacing={2}
                                xs={12}
                                style={{
                                    color: theme.palette.error.main
                                }}
                                alignItems="center"
                            >
                                <Grid item>
                                    <ErrorIcon color="error" />
                                </Grid>
                                <Grid item>
                                    <span>{apiError}</span>
                                </Grid>
                            </Grid>
                        )}
                        <Grid item xs={6}>
                            <TextField
                                label="First name"
                                required
                                type="text"
                                fullWidth
                                value={values.firstname}
                                onChange={ev => handleInputChange(ev, 'firstname')}
                                error={!!errors.firstname}
                                helperText={errors.firstname}
                                disabled={isSubmitting}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Last name"
                                required
                                type="text"
                                fullWidth
                                value={values.lastname}
                                onChange={ev => handleInputChange(ev, 'lastname')}
                                error={!!errors.lastname}
                                helperText={errors.lastname}
                                disabled={isSubmitting}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label={'Contact Phone'}
                                type="tel"
                                value={values.phone}
                                onChange={ev => handleInputChange(ev, 'phone')}
                                onBlur={() => {
                                    if (values.phone) {
                                        const formatted = formatPhone(values.phone);
                                        if (formatted !== values.phone) {
                                            setFieldValue('phone', formatted);
                                        }
                                    }
                                }}
                                disabled={isSubmitting}
                                error={!!errors.phone}
                                helperText={errors.phone}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label={'Email'}
                                value={values.email}
                                onChange={ev => handleInputChange(ev, 'email')}
                                disabled={isSubmitting}
                                error={!!errors.email}
                                helperText={errors.email}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={'Password'}
                                value={values.password}
                                onChange={ev => handleInputChange(ev, 'password')}
                                disabled={isSubmitting}
                                error={!!errors.password}
                                helperText={errors.password}
                                fullWidth
                                required
                                inputProps={{ type: 'password' }}
                            />
                        </Grid>
                        {/* Guest fields in the same order as in clients config */}
                        {requiredGuestFields.map(field => (
                            <Grid item xs={12} sm={6} key={field.id}>
                                <GuestFieldInput
                                    field={field}
                                    values={values}
                                    errors={errors}
                                    setFieldValue={setFieldValue}
                                    handleEventValue={handleGuestFieldEventValue}
                                />
                            </Grid>
                        ))}
                        <Grid item xs={12}>
                            <FormControl>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id="check-term-and-conditions"
                                            checked={values.termsAndConditions}
                                            onChange={ev => {
                                                setFieldValue(
                                                    'termsAndConditions',
                                                    ev.target.checked
                                                );
                                            }}
                                        />
                                    }
                                    label={termsAndConditionsLabel}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <div>
                                Do you have an account already?&nbsp;
                                <a
                                    href="#"
                                    onClick={e => {
                                        e.preventDefault();
                                        showModal(MODAL_TYPE.SIGNIN);
                                    }}
                                >
                                    Sign in
                                </a>
                            </div>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => showModal(MODAL_TYPE.NONE)}>
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting || !isValid || !dirty}
                    >
                        Sign-up
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default withTheme(SignUpModal);
